import { BaseApiService } from './BaseApiService';
import { DateRange } from './DashboardService';

export interface ExpenseItem {
  itemName: string;
  billedQuantity: number;
  totalAmount: number;
  percentageOfLocationTotal?: number; // Item expense as % of location total
  itemExpenseLevel?: string; // HIGH, MEDIUM, or LOW for this item
}

export interface LocationExpense {
  location: string;
  dm: string;
  items: ExpenseItem[];
  totalExpense: number;
  itemCount?: number; // Total Confirmed Qty from "Total" row
  totalSales?: number; // Total sales for the location
  expensePercentageOfSales?: number; // Expense as % of sales
  expenseLevel?: string; // HIGH, MEDIUM, or LOW based on thresholds
}

// Sales tier definitions matching backend SalesTierGrouper
export type SalesTier = 'HIGH_VOLUME' | 'UPPER_MID' | 'LOWER_MID' | 'BASE';

export interface TierGroup {
  tier: SalesTier;
  tierLabel: string;
  tierDescription: string;
  locations: LocationExpense[];
  totalExpense: number;
  totalSales: number;
  avgExpensePercentage: number;
}

// Sales tier thresholds (weekly sales) - matching backend
const HIGH_VOLUME_THRESHOLD = 35000; // > $35k/week
const UPPER_MID_THRESHOLD = 25000;   // $25k-$35k/week
const LOWER_MID_THRESHOLD = 20000;   // $20k-$25k/week

// Tier labels for display
const TIER_LABELS: Record<SalesTier, string> = {
  HIGH_VOLUME: 'High Volume',
  UPPER_MID: 'Upper Mid',
  LOWER_MID: 'Lower Mid',
  BASE: 'Base',
};

const TIER_DESCRIPTIONS: Record<SalesTier, string> = {
  HIGH_VOLUME: '>$35k/week',
  UPPER_MID: '$25k-$35k/week',
  LOWER_MID: '$20k-$25k/week',
  BASE: '<$20k/week',
};

/**
 * Determine sales tier based on weekly sales volume
 */
function determineSalesTier(weeklySales: number): SalesTier {
  if (weeklySales >= HIGH_VOLUME_THRESHOLD) {
    return 'HIGH_VOLUME';
  } else if (weeklySales >= UPPER_MID_THRESHOLD) {
    return 'UPPER_MID';
  } else if (weeklySales >= LOWER_MID_THRESHOLD) {
    return 'LOWER_MID';
  } else {
    return 'BASE';
  }
}

/**
 * Group expenses by sales tier
 * @param locations - Array of LocationExpense
 * @param isQuarterly - If true, divides sales by 13 to get weekly equivalent
 * @returns Array of TierGroup sorted by tier (High Volume first)
 */
export function groupExpensesByTier(
  locations: LocationExpense[],
  isQuarterly: boolean = false
): TierGroup[] {
  const tierGroups: Map<SalesTier, LocationExpense[]> = new Map([
    ['HIGH_VOLUME', []],
    ['UPPER_MID', []],
    ['LOWER_MID', []],
    ['BASE', []],
  ]);

  // Group locations by tier
  for (const location of locations) {
    const sales = location.totalSales ?? 0;
    // For quarterly data, convert to weekly equivalent (÷13 weeks)
    const weeklySales = isQuarterly ? sales / 13 : sales;
    const tier = determineSalesTier(weeklySales);
    tierGroups.get(tier)!.push(location);
  }

  // Convert to TierGroup array and calculate totals
  const result: TierGroup[] = [];
  const tierOrder: SalesTier[] = ['HIGH_VOLUME', 'UPPER_MID', 'LOWER_MID', 'BASE'];

  for (const tier of tierOrder) {
    const tierLocations = tierGroups.get(tier)!;

    // Sort locations by expense % descending within tier
    tierLocations.sort((a, b) =>
      (b.expensePercentageOfSales ?? 0) - (a.expensePercentageOfSales ?? 0)
    );

    const totalExpense = tierLocations.reduce((sum, loc) => sum + loc.totalExpense, 0);
    const totalSales = tierLocations.reduce((sum, loc) => sum + (loc.totalSales ?? 0), 0);
    const avgExpensePercentage = totalSales > 0 ? (totalExpense / totalSales) * 100 : 0;

    result.push({
      tier,
      tierLabel: TIER_LABELS[tier],
      tierDescription: TIER_DESCRIPTIONS[tier],
      locations: tierLocations,
      totalExpense,
      totalSales,
      avgExpensePercentage,
    });
  }

  return result;
}

export class ExpenseService extends BaseApiService {
  private static instance: ExpenseService;

  private constructor() {
    super();
  }

  public static getInstance(): ExpenseService {
    if (!ExpenseService.instance) {
      ExpenseService.instance = new ExpenseService();
    }
    return ExpenseService.instance;
  }

  async getExpenseData(startDate: string, endDate: string): Promise<LocationExpense[]> {
    // Enable caching - smart cache manager will invalidate on expense file changes
    return this.get<LocationExpense[]>(
      `/api/expenses/data?startDate=${startDate}&endDate=${endDate}`,
      undefined,
      true
    );
  }

  async getQuarterlyExpenseData(quarter: string, year?: number): Promise<LocationExpense[]> {
    // Enable caching - smart cache manager will invalidate on quarterly GFS file changes
    const yearParam = year ? `&year=${year}` : '';
    return this.get<LocationExpense[]>(
      `/api/expenses/quarterly-data?quarter=${encodeURIComponent(quarter)}${yearParam}`,
      undefined,
      true
    );
  }

  async getAvailableExpenseWeeks(): Promise<DateRange[]> {
    return this.get<DateRange[]>(`/api/expenses/weeks`, undefined, true);
  }

  async getToastSalesByLocation(startDate: string, endDate: string): Promise<Record<string, number>> {
    return this.get<Record<string, number>>(
      `/api/expenses/toast-sales?startDate=${startDate}&endDate=${endDate}`,
      undefined,
      true
    );
  }

  async getQuarterlyToastSalesByLocation(quarter: string, year: number): Promise<Record<string, number>> {
    return this.get<Record<string, number>>(
      `/api/expenses/quarterly-toast-sales?quarter=${encodeURIComponent(quarter)}&year=${year}`,
      undefined,
      true
    );
  }

  async getItem8WeekAverages(location: string, currentWeekEnd: string): Promise<Record<string, { averageQuantity: number; averageExpense: number }>> {
    return this.get<Record<string, { averageQuantity: number; averageExpense: number }>>(
      `/api/expenses/item-8week-averages?location=${encodeURIComponent(location)}&currentWeekEnd=${currentWeekEnd}`,
      undefined,
      true
    );
  }

  async getItem4WeekAverages(location: string, currentWeekEnd: string): Promise<Record<string, { averageQuantity: number; averageExpense: number }>> {
    return this.get<Record<string, { averageQuantity: number; averageExpense: number }>>(
      `/api/expenses/item-4week-averages?location=${encodeURIComponent(location)}&currentWeekEnd=${currentWeekEnd}`,
      undefined,
      true
    );
  }
}

export default ExpenseService.getInstance();
